import LogRocket from 'logrocket'

let initialized = false

const REDACTED = '[Redacted]'
const DEFAULT_APP_ID = '3wion6/residesk-web-internal'

function isLocalEnvironment() {
  if (import.meta.env.DEV) return true
  const hostname = window.location.hostname
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.localhost')
  )
}

function sanitizeUrl(url) {
  if (!url) return url
  try {
    const parsed = new URL(url, window.location.origin)
    parsed.search = ''
    parsed.hash = ''
    return parsed.toString()
  } catch {
    return url.split('?')[0]
  }
}

function sanitizeHeaders(headers = {}) {
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => {
      const lower = key.toLowerCase()
      if (
        lower === 'authorization' ||
        lower === 'cookie' ||
        lower === 'set-cookie' ||
        lower === 'x-csrf-token'
      ) {
        return [key, REDACTED]
      }
      return [key, value]
    }),
  )
}

function sanitizeRequest(request) {
  return {
    ...request,
    url: sanitizeUrl(request.url),
    headers: sanitizeHeaders(request.headers),
    body: request.body ? REDACTED : request.body,
  }
}

function sanitizeResponse(response) {
  return {
    ...response,
    headers: sanitizeHeaders(response.headers),
    body: response.body ? REDACTED : response.body,
  }
}

/**
 * Boot LogRocket session replay. `VITE_LOGROCKET_APP_ID` can override the
 * default WebInternal project id when an environment needs a separate project.
 * Safe to call before auth resolves and safe to call repeatedly.
 */
export function initLogRocket() {
  if (initialized) return
  if (isLocalEnvironment()) return
  const appId = import.meta.env.VITE_LOGROCKET_APP_ID || DEFAULT_APP_ID
  LogRocket.init(appId, {
    browser: {
      urlSanitizer: sanitizeUrl,
    },
    dom: {
      inputSanitizer: true,
      textSanitizer: '[data-pii]',
    },
    network: {
      requestSanitizer: sanitizeRequest,
      responseSanitizer: sanitizeResponse,
    },
  })
  initialized = true
}

/**
 * Tag the current session with the authenticated user. Call after `useAuth`
 * resolves. No-op if init was skipped or the user record is incomplete.
 *
 * @param {{ email?: string | null, name?: string | null } | null | undefined} user
 */
export function identifyUser(user) {
  if (!initialized || !user?.email) return
  LogRocket.identify(user.email, {
    email: user.email,
    name: user.name ?? '',
  })
}
